import { useMemo, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { ValidationItem } from '@/services/projections'
import {
  collapseValidationItems,
  formatCollapsedValidationRow,
  type CollapsedValidationRow,
} from './groupValidationDisplay'

export type SaveValidationDialogProps = {
  errors: ValidationItem[]
  warnings: ValidationItem[]
  onCancel: () => void
  onConfirm: (acknowledgeWarnings: boolean) => void
}

type GroupedItems = { heading: string; items: CollapsedValidationRow[] }

function groupValidationItems(items: ValidationItem[]): GroupedItems[] {
  const groups = new Map<string, ValidationItem[]>()
  for (const item of items) {
    const heading = item.section?.trim() || 'General'
    const list = groups.get(heading) ?? []
    list.push(item)
    groups.set(heading, list)
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([heading, groupItems]) => ({
      heading,
      items: collapseValidationItems(groupItems),
    }))
}

export default function SaveValidationDialog({
  errors,
  warnings,
  onCancel,
  onConfirm,
}: SaveValidationDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  const errorGroups = useMemo(() => groupValidationItems(errors), [errors])
  const warningGroups = useMemo(() => groupValidationItems(warnings), [warnings])
  const canConfirm = errors.length === 0 && (warnings.length === 0 || acknowledged)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-validation-title"
        className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 id="save-validation-title" className="text-sm font-semibold text-gray-900">
            Review before saving
          </h3>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {errors.length > 0 ? (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-900">Blocking errors</p>
              {errorGroups.map((group) => (
                <div key={group.heading}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.heading}
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {group.items.map((item) => (
                      <li key={`${group.heading}-${item.key}`}>
                        {formatCollapsedValidationRow(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
          {warnings.length > 0 ? (
            <div className={`space-y-4 ${errors.length > 0 ? 'mt-6' : ''}`}>
              <p className="text-sm font-medium text-gray-900">Warnings</p>
              {warningGroups.map((group) => (
                <div key={group.heading}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.heading}
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {group.items.map((item) => (
                      <li key={`${group.heading}-${item.key}`}>
                        {formatCollapsedValidationRow(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                I&apos;ve reviewed these warnings
              </label>
            </div>
          ) : null}
          {errors.length === 0 && warnings.length === 0 ? (
            <p className="text-sm text-gray-600">No validation issues found.</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canConfirm}
            onClick={() => onConfirm(warnings.length > 0 && acknowledged)}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
